import { toast } from "@hortis/ui/sonner";
import { ReactNode } from "@tanstack/react-router";
import axios from "axios";
import {
  type Dispatch,
  HTMLAttributes,
  type SetStateAction,
  useCallback,
} from "react";
import { useImageSelect } from "src/features/user/image-upload/use-image-select";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { uploadFileDocument } from "../../graphql";
import type { MaterialImageSchema } from "../add-plant-material-form/material-image-schema";

export type StagedImage = {
  id: string;
  file?: File;
  thumb: string;
  complete: boolean;
  progress?: number;
  error?: string;
  assetUrl?: string;
} & Omit<MaterialImageSchema, "assetUrl">;

export const AddMaterialPhotosButton = ({
  setImages,
  children,
  className,
  ...props
}: {
  images: Array<StagedImage>;
  setImages: Dispatch<SetStateAction<Array<StagedImage>>>;
  onImageUploaded?: (val: MaterialImageSchema) => void;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) => {
  const uploadFile = useGqlMutation({
    mutation: uploadFileDocument,
  });

  const onFileRejection = useCallback((errors: Array<string>) => {
    toast.error(errors[0]);
  }, []);

  const {
    getRootProps,
    getInputProps,
    errors: _,
  } = useImageSelect({
    maxSize: 20 * 1024 * 1024,
    maxFiles: 10,
    setImage: (image: StagedImage) => {
      setImages((images) => [...images, image]);
    },
    onFileRejection,
    // eslint-disable-next-line sonarjs/cognitive-complexity
    uploadFile: async ({ file, id }) => {
      try {
        const response = await uploadFile.mutateAsync({
          fileName: file.name,
          size: file.size,
          contentType: file.type,
        });

        await axios.put(response.uploadFile.uploadUrl, file, {
          onUploadProgress: (e) => {
            if (e.total == null) {
              return;
            }
            const progressPercent = Math.floor((e.loaded / e.total) * 100);
            setImages((images) =>
              images.map((image) =>
                image.id === id
                  ? {
                      ...image,
                      progress: progressPercent,
                    }
                  : image,
              ),
            );
          },
          headers: {
            ...Object.fromEntries(
              response.uploadFile.headers.map(({ key, value }) => [key, value]),
            ),
            "Content-Type": file.type,
          },
        });

        setTimeout(() => {
          setImages((images) =>
            images.map((image) =>
              image.id === id
                ? {
                    ...image,
                    complete: true,
                    assetUrl: response.uploadFile.uploadUrl.split("?")[0] ?? "",
                  }
                : image,
            ),
          );
        }, 400);
      } catch {
        setImages((images) =>
          images.map((image) =>
            image.id === id
              ? {
                  ...image,
                  error: "Failed to upload image",
                }
              : image,
          ),
        );
        toast.error("Failed to upload image");
      }
    },
  });

  return (
    <div className={className} {...props} {...getRootProps()}>
      {children}
      <input
        {...getInputProps({
          accept: "image/*,text/plain",
          "data-cy": "add-material-photo-input",
        })}
      />
    </div>
  );
};
